import { useEffect, useState } from "react";
import { api, STATUS_LABELS, TASK_STATUSES, fmtDate, initials } from "../lib/api";
import StatusBadge from "../components/StatusBadge";
import { toast } from "sonner";
import { Filter } from "lucide-react";

export default function Tasks() {
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [filterProject, setFilterProject] = useState("all");
  const [view, setView] = useState("kanban");

  const fetchAll = async () => {
    const [t, u, p] = await Promise.all([api.get("/tasks"), api.get("/users"), api.get("/projects")]);
    setTasks(t.data); setUsers(u.data); setProjects(p.data);
  };
  useEffect(() => { fetchAll(); }, []);

  const filtered = tasks.filter((t) => filterProject === "all" || t.project_id === filterProject);

  const updateStatus = async (id, status) => {
    try { await api.patch(`/tasks/${id}/status?status=${status}`); fetchAll(); } catch { toast.error("Erreur"); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.2em] text-btp-terra font-semibold mb-2">Production</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">Tâches & Workflow</h1>
          <p className="text-sm text-slate-500 mt-1">Vue Kanban globale — workflow validation hiérarchique</p>
        </div>
        <div className="flex gap-2">
          <select value={filterProject} onChange={(e) => setFilterProject(e.target.value)} className="h-10 px-3 border border-slate-300 rounded-md text-sm bg-white" data-testid="task-filter-project">
            <option value="all">Tous les projets</option>
            {projects.map((p) => <option key={p.id} value={p.id}>{p.code} — {p.name}</option>)}
          </select>
          <div className="flex border border-slate-300 rounded-md overflow-hidden">
            <button onClick={() => setView("kanban")} className={`px-3 h-10 text-sm ${view === "kanban" ? "bg-btp-navy text-white" : "bg-white text-slate-600"}`} data-testid="view-kanban">Kanban</button>
            <button onClick={() => setView("list")} className={`px-3 h-10 text-sm ${view === "list" ? "bg-btp-navy text-white" : "bg-white text-slate-600"}`} data-testid="view-list">Liste</button>
          </div>
        </div>
      </div>

      {view === "kanban" ? (
        <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-6 gap-3">
          {TASK_STATUSES.map((status) => {
            const cols = filtered.filter((t) => t.status === status);
            return (
              <div key={status} className="kanban-col">
                <div className="flex items-center justify-between mb-3">
                  <div className="text-[11px] uppercase tracking-wider font-bold text-slate-700">{STATUS_LABELS[status]}</div>
                  <span className="text-xs font-mono bg-white border border-slate-200 rounded-full px-2 py-0.5">{cols.length}</span>
                </div>
                <div className="space-y-2">
                  {cols.map((t) => {
                    const assignee = users.find((u) => u.id === t.assigned_to);
                    const project = projects.find((p) => p.id === t.project_id);
                    return (
                      <div key={t.id} className="bg-white border border-slate-200 rounded p-3 text-xs hover:border-btp-navy transition-colors" data-testid={`global-task-${t.id}`}>
                        <div className="font-mono text-[9px] uppercase tracking-wider text-slate-400 mb-1">{project?.code}</div>
                        <div className="font-medium text-slate-900 mb-2 leading-snug">{t.title}</div>
                        <div className="flex justify-between items-center">
                          <div className="text-slate-500 font-mono text-[10px]">{fmtDate(t.deadline)}</div>
                          {assignee && <div className="w-6 h-6 rounded-full bg-btp-navy text-white flex items-center justify-center text-[10px] font-semibold" title={assignee.full_name}>{initials(assignee.full_name)}</div>}
                        </div>
                        <select value={t.status} onChange={(e) => updateStatus(t.id, e.target.value)} className="mt-2 w-full text-[10px] border border-slate-200 rounded px-1 py-0.5">
                          {TASK_STATUSES.map((s) => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
                        </select>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
          <table className="btp-table">
            <thead><tr><th>Projet</th><th>Tâche</th><th>Assigné à</th><th>Échéance</th><th>Priorité</th><th>Statut</th></tr></thead>
            <tbody>
              {filtered.map((t) => {
                const assignee = users.find((u) => u.id === t.assigned_to);
                const project = projects.find((p) => p.id === t.project_id);
                return (
                  <tr key={t.id}>
                    <td className="font-mono text-xs text-slate-500">{project?.code}</td>
                    <td className="font-medium">{t.title}</td>
                    <td>{assignee?.full_name || "—"}</td>
                    <td className="text-xs font-mono">{fmtDate(t.deadline)}</td>
                    <td className="text-xs uppercase">{t.priority}</td>
                    <td><StatusBadge status={t.status} /></td>
                  </tr>
                );
              })}
              {filtered.length === 0 && <tr><td colSpan={6} className="text-center text-slate-400 py-12">Aucune tâche</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
